/**
 * API tokens for the evals MCP server (obsessless-api src/routes/evals/mcp), so Claude Code
 * can read eval suites and runs. Only token issuers can manage them; the API enforces this,
 * and the panel shows a note to anyone else.
 *
 * A new token is shown once, inside the `claude mcp add` command; the API keeps only its
 * hash.
 */
import { KeyRound, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';
import CopyButton from '../../../components/CopyButton';
import InlineErrorDisplay from '../../../components/InlineErrorDisplay';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { API_BASE_URL, ApiError } from '../../../services/apiClient';
import { formatDateTime } from '../../Evals/utils/evalFormat';
import { apiTokensService } from '../services/apiTokensService';
import { MAX_TOKEN_DAYS, type ApiTokenInfo } from '../types/apiTokenTypes';

const INPUT_CLASS = 'px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

/** User scope, so the server works in every folder, not only in a clone of this repo. */
const mcpAddCommand = (token: string): string =>
  `claude mcp add --transport http --scope user obsessless-evals ${API_BASE_URL}/mcp --header "Authorization: Bearer ${token}"`;

/** Why the tokens cannot be managed here, when they cannot. */
const NO_ACCESS: Record<string, string> = {
  'permission-denied': 'Only token issuers can manage API tokens. Ask Adrian for one.',
  'not-found': 'This API does not serve the evals MCP server. It runs only on staging and on a local API.',
};

const StatusBadge = ({ token }: { token: ApiTokenInfo }) => {
  if (token.revokedAt !== null) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Revoked</span>;
  }
  if (token.expiresAt <= Date.now()) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Expired</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Active</span>;
};

export const ApiTokensPanel = () => {
  const [tokens, setTokens] = useState<ApiTokenInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [noAccess, setNoAccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [days, setDays] = useState(MAX_TOKEN_DAYS);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ token: string; tokenInfo: ApiTokenInfo } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await apiTokensService.list();
        if (!cancelled) setTokens(list);
      } catch (caught) {
        if (cancelled) return;
        const reason = caught instanceof ApiError ? NO_ACCESS[caught.code] : undefined;
        if (reason) {
          setNoAccess(reason);
        } else {
          setError(caught instanceof Error ? caught.message : 'Failed to load API tokens.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const email = ownerEmail.trim();
      const result = await apiTokensService.create({ name: name.trim(), days, ...(email ? { ownerEmail: email } : {}) });
      setCreated(result);
      setTokens((current) => [result.tokenInfo, ...current]);
      setName('');
      setOwnerEmail('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to create the API token.');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (tokenId: string) => {
    setError(null);
    try {
      const revoked = await apiTokensService.revoke(tokenId);
      setTokens((current) => current.map((token) => (token.tokenId === tokenId ? revoked : token)));
      if (created?.tokenInfo.tokenId === tokenId) setCreated(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to revoke the API token.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Claude Code: evals</h2>
        <p className="text-sm text-gray-500">
          Tokens for the evals MCP server. With one, Claude Code can read eval suites, runs and results. It cannot start runs or
          change data. A token acts as its owner, who must be an admin.
        </p>
      </div>

      {loading && <LoadingSpinner message="Loading API tokens…" />}
      {noAccess && <p className="text-sm text-gray-700">{noAccess}</p>}

      {created && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-amber-900 break-words min-w-0">
              Token "{created.tokenInfo.name}" for {created.tokenInfo.email}. Copy the command now: the token is not shown again.
            </p>
            <div className="flex gap-2">
              <CopyButton text={mcpAddCommand(created.token)} label="Copy command" />
              <Button variant="ghost" size="sm" onClick={() => setCreated(null)}>
                Done
              </Button>
            </div>
          </div>
          <pre className="text-xs bg-white border border-amber-200 rounded p-2 whitespace-pre-wrap break-all">{mcpAddCommand(created.token)}</pre>
          <p className="text-xs text-amber-900">Run it in a terminal, then start Claude Code and ask it about the eval runs.</p>
        </div>
      )}

      {!loading && !noAccess && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Name</span>
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={100} placeholder="e.g. Laptop" className={INPUT_CLASS} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Owner email</span>
            <input
              type="email"
              value={ownerEmail}
              onChange={(event) => setOwnerEmail(event.target.value)}
              placeholder="Empty: you"
              className={`${INPUT_CLASS} w-64`}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Days</span>
            <input
              type="number"
              min={1}
              max={MAX_TOKEN_DAYS}
              value={days}
              onChange={(event) => setDays(Math.min(MAX_TOKEN_DAYS, Math.max(1, Number(event.target.value) || 1)))}
              className={`${INPUT_CLASS} w-20`}
            />
          </label>
          <Button type="submit" variant="primary" size="sm" icon={<KeyRound size={13} />} disabled={!name.trim() || creating} loading={creating}>
            Create token
          </Button>
        </form>
      )}

      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}

      {tokens.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="font-medium px-4 py-2">Name</th>
                <th className="font-medium px-4 py-2">Owner</th>
                <th className="font-medium px-4 py-2">Created</th>
                <th className="font-medium px-4 py-2">Expires</th>
                <th className="font-medium px-4 py-2">Last used</th>
                <th className="font-medium px-4 py-2">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.tokenId} className="border-t border-gray-100">
                  <td className="px-4 py-2">{token.name}</td>
                  <td className="px-4 py-2">{token.email}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatDateTime(token.createdAt)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatDateTime(token.expiresAt)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{token.lastUsedAt === null ? 'Never' : formatDateTime(token.lastUsedAt)}</td>
                  <td className="px-4 py-2">
                    <StatusBadge token={token} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {token.revokedAt === null && (
                      <ButtonWithConfirmation
                        onClick={() => revoke(token.tokenId)}
                        firstText="Revoke"
                        secondText="Confirm"
                        firstIcon={<Trash2 size={13} />}
                        secondIcon={<Trash2 size={13} />}
                        firstVariant="outline"
                        secondVariant="danger"
                        size="sm"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
